import React, { useRef, useState, useCallback } from "react";
import Slider from "react-slick";
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  Box,
  Divider,
  Dialog,
  DialogContent,
  IconButton,
} from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { useNavigate } from "react-router-dom";
import OpenWithIcon from "@mui/icons-material/OpenWith";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import { useLocation } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import Skeleton from "@mui/material/Skeleton";

const SyndicateGridView = ({ id, name, address, price, image, images = [], area, amenity, nearby, hideNearBy, soldStatus, sold_status }) => {
  const isSold = soldStatus === 1 || sold_status === 1;
  const navigate = useNavigate();
  const previewSliderRef = useRef(null);
  const sliderRef = useRef(null);
  const [imgOpen, setImgOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  // Track which slide is active in the card slider so only that video autoplays
  const [cardSlideIndex, setCardSlideIndex] = useState(0);
  const videoRefs = useRef({});
  const [playingVideo, setPlayingVideo] = useState(null);
  const [hoveredVideo, setHoveredVideo] = useState(null);
  const [loadedMedia, setLoadedMedia] = useState({});

  const handleMediaLoad = (src) => {
    setLoadedMedia((prev) => ({ ...prev, [src]: true }));
  };

  const togglePlay = (e, src) => {
    e.stopPropagation();
    const video = videoRefs.current[src];
    if (video) {
      if (video.paused) {
        if (playingVideo && playingVideo !== src && videoRefs.current[playingVideo]) {
          videoRefs.current[playingVideo].pause();
        }
        video.play();
        setPlayingVideo(src);
      } else {
        video.pause();
        setPlayingVideo(null);
      }
    }
  };

  const location = useLocation();

  const safeAmenity = Array.isArray(amenity) ? amenity : [];
  const safeNearBy = Array.isArray(nearby) ? nearby : [];
  const limitToThree = (arr) => Array.isArray(arr) ? arr.slice(0, 3) : [];
  const amenityList = limitToThree(safeAmenity);
  const nearByList = limitToThree(safeNearBy);

  const allImages = Array.isArray(images) && images.length > 0
    ? images
    : image ? [image] : ["/assets/images/placeholder_bg.png"];

  const showNavArrows = allImages.length > 1;

  const isVideo = (url) => {
    if (typeof url !== 'string') return false;
    return /\.(mp4|webm|mkv|mov|avi)$/i.test(url);
  };

  // Pause ALL videos in a given slider's DOM node
  const pauseVideosIn = (sliderInstance) => {
    const videos = sliderInstance?.innerSlider?.list?.querySelectorAll("video");
    videos?.forEach((v) => { v.pause(); v.currentTime = 0; });
  };

  const handleCardSlideChange = (next) => {
    pauseVideosIn(sliderRef.current);
    setCardSlideIndex(next);
  };

  const handlePreviewSlideChange = (next) => {
    pauseVideosIn(previewSliderRef.current);
    setActiveIndex(next);
  };

  const cardSliderSettings = {
    dots: false,
    arrows: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    infinite: true,
    adaptiveHeight: false,
    beforeChange: (_, next) => handleCardSlideChange(next),
  };

  const handleOpenPreview = (index = 0) => {
    setActiveIndex(index);
    setImgOpen(true);
    setTimeout(() => {
      previewSliderRef.current?.slickGoTo(index);
    }, 100);
  };


  const handleClosePreview = () => {
    // Pause all videos in the preview slider before closing
    pauseVideosIn(previewSliderRef.current);
    setImgOpen(false);
  };

  return (
    <>
      {/* MULTI-IMAGE POPUP DIALOG */}
      <Dialog
        open={imgOpen}
        onClose={handleClosePreview}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: "hidden",
            bgcolor: "rgba(0,0,0,0.85)",
          },
        }}
      >
        <IconButton
          onClick={handleClosePreview}
          sx={{
            position: "absolute",
            top: { xs: 10, sm: 16 },
            right: { xs: 10, sm: 20 },
            zIndex: 10,
            color: "#fff",
            bgcolor: "rgba(0,0,0,0.4)",
            "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
          }}
        >
          <CloseIcon />
        </IconButton>

        <DialogContent sx={{ p: 0 }}>
          {/*
            KEY FIX FOR PREVIEW:
            Lock the outer container to a fixed height.
            Lock all slick internals to 100% so they never
            collapse or jump when a video slide is active.
          */}
          <Box
            sx={{
              position: "relative",
              height: { xs: 300, sm: 480, md: 600 },
              overflow: "hidden",
              // Force every slick wrapper to fill the fixed height
              "& .slick-slider, & .slick-list, & .slick-track": {
                height: "100% !important",
              },
              "& .slick-slide > div": {
                height: "100% !important",
              },
            }}
          >
            {showNavArrows && (
              <IconButton
                onClick={() => previewSliderRef.current?.slickPrev()}
                sx={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", zIndex: 3, color: "#fff", bgcolor: "rgba(0,0,0,0.5)", "&:hover": { bgcolor: "rgba(0,0,0,0.75)" } }}
              >
                <ArrowBackIosNewIcon />
              </IconButton>
            )}

            <Slider
              ref={previewSliderRef}
              initialSlide={activeIndex}
              beforeChange={(_, next) => handlePreviewSlideChange(next)}
              dots
              infinite
              speed={400}
              slidesToShow={1}
              slidesToScroll={1}
              arrows={false}
              adaptiveHeight={false}
            >
              {allImages.map((img, i) => (
                // Each slide inner Box must also be 100% height
                <Box key={i} sx={{ height: "100%" }}>
                  <Box
                    sx={{
                      height: { xs: 300, sm: 480, md: 600 },
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "rgba(0,0,0,0.7)",
                      p: 1,
                      overflow: "hidden",
                      position: "relative"
                    }}
                  >
                    {!loadedMedia[img] && (
                      <Skeleton
                        variant="rectangular"
                        width="100%"
                        height="100%"
                        sx={{ position: "absolute", top: 0, left: 0, bgcolor: "rgba(255,255,255,0.1)" }}
                      />
                    )}
                    {isVideo(img) ? (
                      <video
                        key={`preview-video-${i}`}
                        src={img}
                        controls
                        playsInline
                        disablePictureInPicture
                        disableRemotePlayback
                        controlsList="nodownload nopictureinpicture"
                        onLoadedData={() => handleMediaLoad(img)}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                          display: loadedMedia[img] ? "block" : "none",
                        }}
                      />
                    ) : (
                      <img
                        src={img}
                        alt={`${name || ""} ${i + 1}`}
                        onLoad={() => handleMediaLoad(img)}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                          display: loadedMedia[img] ? "block" : "none",
                        }}
                      />
                    )}
                  </Box>
                </Box>
              ))}
            </Slider>

            {showNavArrows && (
              <IconButton
                onClick={() => previewSliderRef.current?.slickNext()}
                sx={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", zIndex: 3, color: "#fff", bgcolor: "rgba(0,0,0,0.5)", "&:hover": { bgcolor: "rgba(0,0,0,0.75)" } }}
              >
                <ArrowForwardIosIcon />
              </IconButton>
            )}
            {showNavArrows && (
              <Box
                sx={{
                  position: "absolute", bottom: 12, left: "50%", transform: "translateX(-50%)",
                  bgcolor: "rgba(0,0,0,0.5)", color: "#fff", borderRadius: "12px",
                  px: 1.5, py: 0.3, fontSize: 13, pointerEvents: "none", zIndex: 3,
                }}
              >
                {activeIndex + 1} / {allImages.length}
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      <Card
        onClick={() => navigate(`/syndicate-plots/details/${id}`)}
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          p: 1.5,
          borderRadius: 5,
          overflow: "hidden",
          backgroundImage: "url('/assets/images/syndicate_bg_1.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          cursor: "pointer",
          transition: "transform 0.22s ease, box-shadow 0.22s ease",
          "&:hover": {
            transform: "translateY(-5px) scale(1.01)",
            boxShadow: "0 12px 30px rgba(0,0,0,0.15)",
          },
        }}
      >
        <Box onClick={(e) => e.stopPropagation()} sx={{ position: "relative" }}>
          {isSold && (
            <Box
              sx={{
                position: "absolute",
                top: 10,
                left: 10,
                bgcolor: "#d32f2f",
                color: "#fff",
                px: 2,
                py: 0.6,
                borderRadius: 1.5,
                fontSize: 13,
                fontWeight: 800,
                zIndex: 3,
                textTransform: "uppercase",
                boxShadow: "0 4px 12px rgba(0,0,0,0.45)",
                letterSpacing: "0.5px",
              }}
            >
              Sold Out
            </Box>
          )}
          <Box
            sx={{
              opacity: isSold ? 0.6 : 1,
              filter: isSold ? "grayscale(20%)" : "none",
            }}
          >
            {allImages.length > 1 ? (
              <Box
                sx={{
                  position: "relative",
                  height: 180,
                  // Lock all slick internals so video never drives container height
                  "& .slick-slider, & .slick-list, & .slick-track, & .slick-slide > div": {
                    height: "100% !important",
                  },
                  "& .slick-slide > div": {
                    height: "100% !important",
                  },
                }}
              >
              <Slider 
                ref={sliderRef} 
                {...cardSliderSettings}
                beforeChange={() => {
                  if (playingVideo) {
                    const video = document.querySelector(`video[src="${playingVideo}"]`);
                    if (video) video.pause();
                    setPlayingVideo(null);
                  }
                }}
              >
                {allImages.map((img, i) => (
                  // height: "100%" on inner Box prevents collapse between slides
                   <Box key={i} sx={{ height: "100%" }}>
                    {isVideo(img) ? (
                      <Box
                        onMouseEnter={() => setHoveredVideo(img)}
                        onMouseLeave={() => setHoveredVideo(null)}
                        sx={{ position: "relative", height: 180 }}
                      >
                        {!loadedMedia[img] && (
                          <Skeleton
                            variant="rectangular"
                            width="100%"
                            height="100%"
                            sx={{ position: "absolute", top: 0, left: 0, borderRadius: 4 }}
                          />
                        )}
                        <video
                          ref={(el) => (videoRefs.current[img] = el)}
                          src={img}
                          playsInline
                          disablePictureInPicture
                          disableRemotePlayback
                          controlsList="nodownload nopictureinpicture"
                          onLoadedData={() => handleMediaLoad(img)}
                          onClick={(e) => {
                            if (playingVideo !== img) {
                              togglePlay(e, img);
                            } else {
                              handleOpenPreview(i);
                            }
                          }}
                          style={{
                            width: "100%",
                            height: 180,
                            borderRadius: 4,
                            objectFit: "cover",
                            cursor: "pointer",
                            display: loadedMedia[img] ? "block" : "none",
                          }}
                        />
                        {loadedMedia[img] && (playingVideo !== img || hoveredVideo === img) && (
                          <IconButton
                            onClick={(e) => togglePlay(e, img)}
                            sx={{
                              position: "absolute",
                              top: "50%",
                              left: "50%",
                              transform: "translate(-50%, -50%)",
                              color: "#fff",
                              bgcolor: "rgba(0,0,0,0.5)",
                              borderRadius: "4px",
                              "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                            }}
                          >
                            {playingVideo === img ? (
                              <PauseIcon sx={{ fontSize: 40 }} />
                            ) : (
                              <PlayArrowIcon sx={{ fontSize: 40 }} />
                            )}
                          </IconButton>
                        )}
                      </Box>
                    ) : (
                      <Box
                 onMouseEnter={() => setHoveredVideo(allImages[0])}
                 onMouseLeave={() => setHoveredVideo(null)}
                 sx={{ position: "relative", height: 180 }}
               >
                        {!loadedMedia[img] && (
                          <Skeleton
                            variant="rectangular"
                            width="100%"
                            height="100%"
                            sx={{ position: "absolute", top: 0, left: 0, borderRadius: 4 }}
                          />
                        )}
                        <CardMedia
                          component="img"
                          image={img}
                          alt={name}
                          onLoad={() => handleMediaLoad(img)}
                          onClick={(e) => { e.stopPropagation(); handleOpenPreview(i); }}
                          sx={{
                            height: 180,
                            borderRadius: 4,
                            objectFit: "cover",
                            cursor: "zoom-in",
                            display: loadedMedia[img] ? "block" : "none",
                            transition: "transform 0.3s ease",
                            "&:hover": { transform: "scale(1.04)" },
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                ))}
              </Slider>

              <IconButton
                onClick={(e) => { e.stopPropagation(); sliderRef.current?.slickPrev(); }}
                sx={{
                  position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)",
                  zIndex: 2, bgcolor: "rgba(255,255,255,0.7)", p: 0.5,
                  "&:hover": { bgcolor: "white" }
                }}
              >
                <ArrowBackIosNewIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <IconButton
                onClick={(e) => { e.stopPropagation(); sliderRef.current?.slickNext(); }}
                sx={{
                  position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
                  zIndex: 2, bgcolor: "rgba(255,255,255,0.7)", p: 0.5,
                  "&:hover": { bgcolor: "white" }
                }}
              >
                <ArrowForwardIosIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <Box
                sx={{
                  position: "absolute", bottom: 8, right: 8,
                  bgcolor: "rgba(0,0,0,0.55)", color: "#fff",
                  fontSize: 11, fontWeight: 700, borderRadius: "6px",
                  px: 1, py: 0.3, pointerEvents: "none", zIndex: 2,
                }}
              >
                +{allImages.length - 1} more
              </Box>
            </Box>
          ) : (
             isVideo(allImages[0]) ? (
              <Box sx={{ position: "relative", height: 180 }}>
                {!loadedMedia[allImages[0]] && (
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    height="100%"
                    sx={{ position: "absolute", top: 0, left: 0, borderRadius: 4 }}
                  />
                )}
                <video
                  ref={(el) => (videoRefs.current[allImages[0]] = el)}
                  src={allImages[0]}
                  playsInline
                  disablePictureInPicture
                  disableRemotePlayback
                  controlsList="nodownload nopictureinpicture"
                  onLoadedData={() => handleMediaLoad(allImages[0])}
                  onClick={(e) => {
                    if (playingVideo !== allImages[0]) {
                      togglePlay(e, allImages[0]);
                    } else {
                      handleOpenPreview(0);
                    }
                  }}
                  style={{
                    width: "100%",
                    height: 180,
                    borderRadius: 4,
                    objectFit: "cover",
                    cursor: "pointer",
                    display: loadedMedia[allImages[0]] ? "block" : "none",
                  }}
                />
                {loadedMedia[allImages[0]] && (playingVideo !== allImages[0] || hoveredVideo === allImages[0]) && (
                  <IconButton
                    onClick={(e) => togglePlay(e, allImages[0])}
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      color: "#fff",
                      bgcolor: "rgba(0,0,0,0.5)",
                      borderRadius: "4px",
                      "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                    }}
                  >
                    {playingVideo === allImages[0] ? (
                      <PauseIcon sx={{ fontSize: 40 }} />
                    ) : (
                      <PlayArrowIcon sx={{ fontSize: 40 }} />
                    )}
                  </IconButton>
                )}
              </Box>
            ) : (
              <Box sx={{ position: "relative", height: 180 }}>
                {!loadedMedia[allImages[0]] && (
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    height="100%"
                    sx={{ position: "absolute", top: 0, left: 0, borderRadius: 4 }}
                  />
                )}
                <CardMedia
                  component="img"
                  image={allImages[0]}
                  alt={name}
                  onLoad={() => handleMediaLoad(allImages[0])}
                  onClick={(e) => { e.stopPropagation(); handleOpenPreview(0); }}
                  sx={{
                    height: 180,
                    borderRadius: 4,
                    objectFit: "cover",
                    cursor: "zoom-in",
                    display: loadedMedia[allImages[0]] ? "block" : "none",
                    transition: "transform 0.3s ease",
                    "&:hover": { transform: "scale(1.04)" },
                  }}
                />
              </Box>
            )
          )}
          </Box>
        </Box>

        <CardContent sx={{ flexGrow: 1, overflow: "hidden", opacity: isSold ? 0.65 : 1 }}>
          <Typography
            fontWeight={600}
            noWrap
            sx={{
              fontSize: "clamp(13px, 1.1vw, 16px)",
              lineHeight: 1.25,
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {name}
          </Typography>
          <Box display="flex" alignItems="center" mt={0.5} mb={1}>
            <LocationOnIcon sx={{ fontSize: 18, color: "#9DC354", mr: 0.5, flexShrink: 0 }} />
            <Typography
              variant="body2"
              color="text.secondary"
              noWrap
              sx={{ fontSize: "clamp(11px, 1vw, 13px)", overflow: "hidden", textOverflow: "ellipsis" }}
            >
              {address}
            </Typography>
          </Box>

          <Divider sx={{ borderStyle: "dashed", borderWidth: 1, borderColor: "#FDBA15" }} />
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1, gap: 2, width: "100%" }}>
            <Box sx={{ flex: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, whiteSpace: "nowrap" }}>
                <CurrencyRupeeIcon sx={{ fontSize: { xs: 14, sm: 14 }, color: "#9DC354", flexShrink: 0 }} />
                <Typography sx={{ fontWeight: 600, fontSize: { xs: 13, sm: 14, md: 14, lg: 14 } }}>
                  {price || ""}
                </Typography>
                <Typography sx={{ fontSize: { xs: 11, sm: 12, md: 13 }, color: "text.secondary" }}>
                  /Sq.ft
                </Typography>
              </Box>
              <Typography sx={{ mt: 0.3, fontSize: { xs: 11, sm: 12, md: 13 }, color: "text.secondary", textAlign: "left" }}>
                Per Sq.ft
              </Typography>
            </Box>

            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, whiteSpace: "nowrap" }}>
                <OpenWithIcon sx={{ fontSize: { xs: 14, sm: 13 }, color: "#9DC354", flexShrink: 0 }} />
                <Typography sx={{ fontWeight: 600, fontSize: { xs: 13, sm: 14, md: 14, lg: 14 } }}>
                  {area || ""}
                </Typography>
                <Typography sx={{ fontSize: { xs: 11, sm: 12, md: 13 }, color: "text.secondary" }}>
                  Sq.ft
                </Typography>
              </Box>
              <Typography sx={{ mt: 0.3, fontSize: { xs: 11, sm: 12, md: 13 }, color: "text.secondary", textAlign: "left" }}>
                Total Area
              </Typography>
            </Box>
          </Box>
          <Divider sx={{ borderStyle: "dashed", borderWidth: 1, borderColor: "#FDBA15", marginTop: 1 }} />

          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mt: 1 }}>
            <Typography sx={{ minWidth: 90, fontWeight: 500, fontSize: { xs: 12, sm: 13, md: 14 }, lineHeight: "22px", whiteSpace: "nowrap" }}>
              Amenities:
            </Typography>
            <Typography sx={{ fontSize: 14, lineHeight: "22px", color: "text.secondary", flex: 1, mb: 1, overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 1, WebkitBoxOrient: "vertical" }}>
              {amenityList.length > 0
                ? amenityList.map((item, index) => (
                  <span key={item.id}>{item.title}{index !== amenityList.length - 1 && " • "}</span>
                ))
                : "No nearby amenities"}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mt: 1 }}>
            <Typography sx={{ minWidth: 90, fontWeight: 500, fontSize: { xs: 12, sm: 13, md: 14 }, lineHeight: "22px", whiteSpace: "nowrap" }}>
              Nearby:
            </Typography>
            <Typography sx={{ fontSize: 14, lineHeight: "22px", color: "text.secondary", flex: 1, mb: 1, overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 1, WebkitBoxOrient: "vertical" }}>
              {nearByList.length > 0
                ? nearByList.map((item, index) => (
                  <span key={item.id || index}>{item.title}{index !== nearByList.length - 1 && " • "}</span>
                ))
                : "No nearby locations"}
            </Typography>
          </Box>
        </CardContent>

        <Button
          variant="contained"
          sx={{
            mt: 1,
            bgcolor: "#FDBA15",
            color: "#000",
            borderRadius: 20,
            fontSize: "clamp(12px, 1vw, 14px)",
            "&:hover": { bgcolor: "#e3a30f" },
            whiteSpace: "nowrap",
            opacity: isSold ? 0.65 : 1,
          }}
          onClick={(e) => { e.stopPropagation(); navigate(`/syndicate-plots/details/${id}`); }}
        >
          View Details
        </Button>
      </Card>
    </>
  );
};

export default SyndicateGridView;